import DOM from '../DOM/DOM'
import { is } from '../is/is'
import { Object } from '../Object/Object'

import UserAgent from '../UserAgent/UserAgent'
import BaseSoundSystem from '../BaseSoundSystem/BaseSoundSystem'

import log from '../Logger/log'

import getAudio from './utils/getAudio'
import hasFreeTrack from './utils/hasFreeTrack'

import type {
  IAudio, IFadeParams, TTrackLimit, IPlayParams, TAudioFormat,
  IAudioSource, TAudioSources, TPreloadedAudio, TPreloadedAudios,
  TPlayingAudioData, IWebAudioAddParams
} from './definitions'

import { DEFAULT_VOLUME } from './definitions'

export default abstract class BaseWebAudioSoundSystem
  extends BaseSoundSystem<IAudio, TPlayingAudioData, IWebAudioAddParams> {
  /** Hány hangsávot indíhatunk el egy hangforráshoz. */
  protected maxTracksPerSource?: number
  /** Kulcsonként beállítható max. Hangsávhasználat. */
  protected customMaxTracksPerSource: TTrackLimit = {}

  private readonly preloadedAudios: TPreloadedAudios = { mp3: {}, ogg: {} }

  private audioSources: TAudioSources = {}

  /**
   * Preloaded audiok tárolása.
   * @param preloadedAudios - Preloaded audios.
   */
  public setPreloadedAudios (preloadedAudios: TPreloadedAudio): void {
    const saf = this.supportedAudioFormat

    if (saf) {
      const keys = Object.getKeys(preloadedAudios)

      keys.forEach((src) => {
        const isMp3 = src.endsWith('.mp3')

        const mp3 = isMp3 ? src : undefined
        const ogg = isMp3 ? undefined : src

        const validSrc = saf === 'mp3'
          ? mp3
          : ogg

        if (
          is.string(validSrc) &&
          is.undefined(this.preloadedAudios[saf][src])
        ) {
          this.preloadedAudios[saf][src] = preloadedAudios[src]
        }
      })
    }
  }

  /**
   * Hang hosszának elkérése.
   * @param key - A hangfájl kulcsa.
   */
  public getAudioDuration (key: string): number | null {
    const saf = this.supportedAudioFormat

    if (!saf) {
      return null
    }

    const audioData = this.allAudios[saf][key]

    if (is.undefined(audioData)) {
      return null
    }

    return audioData.audio.duration()
  }

  /**
   * Hang lejátszásának szüneteltetése.
   * @param key - A hangfájl kulcsa.
   * @param id  - Lejátszás id-ja.
   */
  public pause (key: string, id?: number): void {
    const saf = this.supportedAudioFormat
    const playingAudios = this.allPlayingAudios[key]

    if (!saf || is.undefined(playingAudios)) {
      return
    }

    const validPlayingAudios = playingAudios.filter((data) => (
      (is.undefined(id) || id === data.id) &&
      !data.isPaused
    ))

    const audioData = this.allAudios[saf][key]

    if (is.undefined(audioData)) {
      return
    }

    validPlayingAudios.forEach((props) => {
      props.isPaused = true

      audioData.audio.pause(props.id)
    })
  }

  /**
   * Hangfájl lejátszásának folytatása.
   * @param key - A hangfájl kulcsa.
   * @param id  - Lejátszás id-ja.
   */
  public resume (key: string, id?: number): void {
    const saf = this.supportedAudioFormat

    const playingAudios = this.allPlayingAudios[key]

    if (!saf || is.undefined(playingAudios)) {
      return
    }

    const validPlayingAudios = playingAudios.filter((data) => (
      (is.undefined(id) || id === data.id) &&
      data.isPaused
    ))

    const audioData = this.allAudios[saf][key]

    if (is.undefined(audioData)) {
      return
    }

    validPlayingAudios.forEach((props) => {
      props.isPaused = false

      audioData.audio.play(props.id)
    })
  }

  /**
   * Hang lejátszásának megállítása.
   * @param key - A hangfájl kulcsa.
   * @param id  - Lejátszáshoz tartozó id.
   */
  public stop (key: string, id?: number): void {
    const saf = this.supportedAudioFormat

    const playingAudios = this.allPlayingAudios[key]

    if (!saf || is.undefined(playingAudios)) {
      return
    }

    const audioData = this.allAudios[saf][key]

    if (is.undefined(audioData)) {
      return
    }

    const validPlayingAudios = playingAudios.filter((data) => is.undefined(id) || data.id === id)

    validPlayingAudios.forEach((props) => {
      audioData.audio.stop(props.id)

      this.onAudioPlayEnded(props.id, key)
    })
  }

  /**
   * Hang hangerejének beállítása.
   * @param key    - A hangfájl kulcsa.
   * @param volume - Hangerő.
   */
  public setAudioVolume (key: string, volume: number): void {
    const saf = this.supportedAudioFormat

    if (saf) {
      const audioData = this.allAudios[saf][key]

      if (is.undefined(audioData)) {
        return
      }

      audioData.volume = volume
      audioData.audio.volume(volume)
    }
  }

  /**
   * Hang hangerejének beállítása.
   * @param key - A hangfájl kulcsa.
   */
  public getAudioVolume (key: string): number {
    const saf = this.supportedAudioFormat

    if (saf) {
      const audioData = this.allAudios[saf][key]

      const volume = audioData
        ? audioData.volume
        : DEFAULT_VOLUME

      return volume
    }

    return DEFAULT_VOLUME
  }

  /**
   * Szerepel az allPlayingAudios és szüneteltetve van a lejátszás?
   * @param key - A hangfájl kulcsa.
   * @param id  - Hang lejátszás id-ja.
   */
  public isPaused (key: string, id: number): boolean {
    const playingAudioData = this.allPlayingAudios[key]

    if (playingAudioData) {
      const validAudioData = playingAudioData.find((dt) => dt.id === id)

      return validAudioData
        ? validAudioData.isPaused
        : false
    }

    return false
  }

  /**
   * A lejátszott hangot lehalkítja/hangosítja az adott időintervallumon beül.
   * @param params - Params.
   */
  public async fade (
    params: IFadeParams
  ): Promise<void> {
    const { delay, duration, from, id, key, needToStoreVolume = false, to } = params

    const saf = this.supportedAudioFormat
    const playingAudios = this.allPlayingAudios[key]

    if (!saf || is.undefined(playingAudios)) {
      return
    }

    const audioData = this.allAudios[saf][key]

    if (is.undefined(audioData)) {
      return
    }

    const validPlayingAudios = playingAudios.filter((props) => (
      is.undefined(id) || props.id === id
    ))

    if (is.empty(validPlayingAudios)) {
      return
    }

    if (is.number(delay)) {
      await this.wait(delay)
    }

    const { audio, volume } = audioData

    const startVolume = is.undefined(from) ? volume : from

    await Promise.all(
      validPlayingAudios.map((playingAudioProps): Promise<void> => {
        const { id } = playingAudioProps

        return new Promise((resolve) => {
          // Az előzőleg beállított fade eventet töröljük.
          audio.off('fade')

          audio.once('fade', () => {
            if (needToStoreVolume) {
              /** !!! HACKY, de így lehet megoldani. {@link https://github.com/goldfire/howler.js/issues/539}. */
              audioData.volume = to
              audio.volume(to)
            }

            // Ha a hangerőt 0-ra fadeeltük, akkor megállítjuk a hangot.
            if (to === 0) {
              this.stop(key, id)
            }

            /** Normál esetben a fade végén, be kéne állítaniai a hangerőt, de ez nem történik meg. */
            resolve()
          })

          audio.fade(startVolume, to, duration, id)
        })
      })
    )
  }

  /**
   * Létezik előre példányosított Howl?
   * @param src - Hangforrás.
   */
  public isPreloaded (src: string): boolean {
    const saf = this.supportedAudioFormat

    if (!saf) {
      return false
    }

    // létezik ilyen kulcs az object-ben
    return src in this.preloadedAudios[saf]
  }

  /**
   * Hangfájl hozzáadása.
   * @param params - Params.
   */
  protected async addAudio (params: IWebAudioAddParams): Promise<void> {
    const saf = this.supportedAudioFormat

    if (!saf) {
      return
    }

    const { isOnlyPreloaded = false, key, limit, mp3, ogg, volume } = params

    const src = saf === 'mp3'
      ? mp3
      : ogg

    if (is.undefined(src)) {
      return
    }

    if (!is.undefined(this.allAudios[saf][key])) {
      return
    }

    // Ha van source,
    // és még nincs hozzáadva

    const vol = volume ?? DEFAULT_VOLUME

    if (is.number(limit)) {
      this.customMaxTracksPerSource[key] = limit
    }

    // Csak preloadolt hangokat engedünk hozzáadni.
    if (isOnlyPreloaded) {
      // Ha van preloadolt hang, akkor azt használjuk.
      if (this.isPreloaded(src)) {
        const audio = this.preloadedAudios[saf][src]

        this.allAudios[saf][key] = { audio, volume: vol }

        return
      }

      // Ha nincs preloadolt hang, akkor tároljuk az adatokat.
      this.audioSources[key] = { src, volume: vol }

      return
    }

    const audio = this.isPreloaded(src)
      ? this.preloadedAudios[saf][src]
      : await getAudio(src, vol)

    if (audio) {
      this.allAudios[saf][key] = { audio, volume: vol }
    }
  }

  /**
   * Hang lejátszása.
   * @param params - Params.
   * @param saf    - AudioFormat.
   */
  protected playAudio (
    params: IPlayParams,
    saf: TAudioFormat
  ): void | number {
    const { canLoop = false, currentTime, isForce = false, key, onEnd, volume } = params

    const isPlaying = key in this.allPlayingAudios

    // ha épp folyamatban van a hangnak a lejátszása, és nem force, akkor nincs mit kezdeni vele.
    if (isPlaying && !isForce) {
      if (onEnd) {
        onEnd()
      }

      return
    }

    // Már folyamatban van a példányosítás.
    if (this.audioSources[key] === 'in-progress') {
      return
    }

    if (this.audioSources[key]) {
      // eslint-disable-next-line no-restricted-syntax -- Itt megengedjük.
      this.handleAudioSource(key, this.audioSources[key]).then(() => {
        // Itt nem küldjük vissza a lejátszás id-ját,
        // mert ez „tűzoltás”.
        this.playAudio(params, saf)
      })

      return
    }

    // A konkrét audioData
    const audioData = this.allAudios[saf][key]

    // ha nem létezik a kulcshoz audioData, akkor resolve
    if (is.undefined(audioData)) {
      if (onEnd) {
        onEnd()
      }

      return
    }

    // ha nincs szabad sáv, akkor leállítjuk a leghamarabb indítottat.
    if (!this.hasFreeTrack(key)) {
      this.stopOldestTrack(key)
    }

    const { audio } = audioData

    const id = audio.play()

    const vol = volume ?? audioData.volume

    audio.volume(vol, id)

    if (currentTime) {
      audio.seek(currentTime, id)
    }

    audio.on('playerror', () => {
      log('playerror', key)
    }, id)

    audio.on('end', () => {
      this.onAudioPlayEnded(id, key)

      if (onEnd) {
        onEnd()
      }

      if (canLoop) {
        this.play(params)
      }
    }, id)

    // Ha nem tartozik a kulcshoz még érték
    if (is.undefined(this.allPlayingAudios[key])) {
      this.allPlayingAudios[key] = []
    }

    this.allPlayingAudios[key].push({ id, isPaused: false })

    return id
  }

  /** Az összes hang elnémítása. */
  protected mute (): void {
    this.onMute(true)
  }

  /** Az összes hang némításának feloldása. */
  protected unmute (): void {
    this.onMute(false)
  }

  /**
   * Audio példányosításának kezelése.
   * @param key  - Kulcs.
   * @param data - Adatok.
   */
  private async handleAudioSource (key: string, data: IAudioSource): Promise<void> {
    const { src, volume } = data

    // Null-ra állítjuk, ezzel „jelezve”, hogy már folyamatban van a példányosítás.
    this.audioSources[key] = 'in-progress'

    await this.add({
      key,
      mp3: src,
      volume
    })

    // eslint-disable-next-line @typescript-eslint/no-dynamic-delete
    delete this.audioSources[key]
  }

  /**
   * Némítás kezelése.
   * @param isMute - Elnémítunk?
   */
  private onMute (isMute: boolean): void {
    const saf = this.supportedAudioFormat

    if (!saf) {
      return
    }

    // Ha némítunk, akkor azt minden eszközön ugyanúgy tesszük.
    if (isMute) {
      this.handleAudioMute(isMute, saf)

      return
    }

    const { isApple, isHandheld } = UserAgent.getInstance()

    // Apple eszközökön csak akkor engedjük folytatni a hangok lejátszását,
    // ha a felhasználó interakcióba lép az oldallal.
    if (isApple) {
      const eventName = isHandheld ? 'touchstart' : 'click'

      DOM.once(document.body, eventName, () => {
        this.handleAudioMute(isMute, saf)
      })

      return
    }

    this.handleAudioMute(isMute, saf)
  }

  /**
   * Hangok elnémítésa, indítása.
   * @param isMute - Elnémítunk?
   * @param saf    - AudioFormat.
   */
  private handleAudioMute (isMute: boolean, saf: TAudioFormat): void {
    // Lejátszás alatt lévő hangok kulcsai.
    const playingAudioKeys = Object.getKeys(this.allPlayingAudios)

    playingAudioKeys.forEach((key) => {
      // A kulcshoz tartozó lejátszás alatt lévő hangsávok.
      const playingAudios = this.allPlayingAudios[key]

      playingAudios.forEach((playingAudio) => {
        if (playingAudio.isPaused) {
          return
        }

        // Ha nincs pauzálva az adott hangsáv
        const { id } = playingAudio

        const audioData = this.allAudios[saf][key]

        if (is.undefined(audioData)) {
          return
        }

        // ha létezik hozzá audioforrás (tuti van).
        const { audio } = audioData

        // némításnál
        if (isMute) {
          audio.pause(id)

          return
        }

        // folytatásnál
        audio.play(id)
      })
    })
  }

  /**
   * Használt sávok számának elkérése, kulcs alapján.
   * @param key - Hangforrás kulcsa.
   */
  private getUsedTracksByKey (key: string): number {
    return this.allPlayingAudios[key]?.length ?? 0
  }

  /**
   * Van még szabad sáv a kulcshoz?
   * @param key - Hangforrás kulcsa.
   */
  private hasFreeTrack (key: string): boolean {
    const usedTracks = this.getUsedTracksByKey(key)

    return hasFreeTrack(key, usedTracks, this.customMaxTracksPerSource, this.maxTracksPerSource)
  }

  /**
   * A kulcshoz tartozó, legkorábban indított sáv leállítása.
   * @param key - A hangforrás kulcsa.
   */
  private stopOldestTrack (key: string): void {
    const playingAudios = this.allPlayingAudios[key]

    if (playingAudios) {
      const [ oldestTrack ] = playingAudios

      this.stop(key, oldestTrack.id)
    }
  }

  /**
   * Audio lejátszás végénél pucoljuk az allPlayingAudios-t.
   * @param id  - Lejátszás id-ja.
   * @param key - Lejátsztott audiohoz tartozó kulcs.
   */
  private onAudioPlayEnded (id: number, key: string): void {
    if (is.undefined(this.allPlayingAudios[key])) {
      return
    }

    const newPlayingAudios = this.allPlayingAudios[key].filter((dt) => dt.id !== id)

    if (is.empty(newPlayingAudios)) {
      // eslint-disable-next-line @typescript-eslint/no-dynamic-delete
      delete this.allPlayingAudios[key]

      return
    }

    this.allPlayingAudios[key] = newPlayingAudios
  }
}
